/**
 * PlaybookLibrary — backwards-compatible thin wrapper over SwimLaneBoard.
 *
 * Phase 7 replaced the static SWIM_LANES grouping with the live `/swim-lanes`
 * endpoint that supports four organize-by modes plus a Briefs↔Creatives
 * container toggle. The exported SWIM_LANES + getSwimLane helpers remain so
 * existing call sites (modal defaults, route params) keep working.
 */

import { Sparkles, Plus } from 'lucide-react';
import {
  Target,
  Zap,
  Heart,
  Rocket,
  Calendar,
  Trophy,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import type { GenerationBrief } from '@/services/api';
import { SwimLaneBoard } from './SwimLaneBoard';
import type { SwimLaneData } from './SwimLane';

// ---------------------------------------------------------------------------
// Public taxonomy (kept intact for external consumers — modal pre-fill, etc.)
// ---------------------------------------------------------------------------

export interface SwimLane {
  id: string;
  label: string;
  icon: LucideIcon;
  iconColor: string;
  description: string;
  campaignType: string;
}

export const SWIM_LANES: SwimLane[] = [
  {
    id: 'acquisition',
    label: 'Acquisition',
    icon: Target,
    iconColor: 'text-teal-500',
    description: 'Cold-traffic prospecting and awareness',
    campaignType: 'acquisition',
  },
  {
    id: 'conversion',
    label: 'Conversion',
    icon: Zap,
    iconColor: 'text-indigo-500',
    description: 'Warm retargeting and purchase-intent',
    campaignType: 'conversion',
  },
  {
    id: 'retention',
    label: 'Retention',
    icon: Heart,
    iconColor: 'text-rose-500',
    description: 'Loyalty, upsell, and win-back',
    campaignType: 'retention',
  },
  {
    id: 'product_launch',
    label: 'Product Launch',
    icon: Rocket,
    iconColor: 'text-amber-500',
    description: 'New product introductions',
    campaignType: 'product_launch',
  },
  {
    id: 'seasonal',
    label: 'Seasonal',
    icon: Calendar,
    iconColor: 'text-sky-500',
    description: 'Holiday and seasonal moments',
    campaignType: 'seasonal',
  },
  {
    id: 'competitive',
    label: 'Competitive',
    icon: Trophy,
    iconColor: 'text-emerald-500',
    description: 'Conquest and comparison campaigns',
    campaignType: 'competitive',
  },
  {
    id: 'drafts',
    label: 'Drafts & Misc',
    icon: FileText,
    iconColor: 'text-slate-400',
    description: 'Unclassified and work-in-progress',
    campaignType: 'drafts',
  },
];

const SWIM_LANE_MAP: Record<string, string> = {
  awareness: 'acquisition',
  prospecting: 'acquisition',
  acquisition: 'acquisition',
  consideration: 'conversion',
  conversion: 'conversion',
  retention: 'retention',
  loyalty: 'retention',
  product_launch: 'product_launch',
  launch: 'product_launch',
  seasonal: 'seasonal',
  holiday: 'seasonal',
  competitive: 'competitive',
  conquest: 'competitive',
};

export function getSwimLane(brief: GenerationBrief): string {
  const ct = brief.objective?.campaign_type?.toLowerCase()?.trim() ?? '';
  return SWIM_LANE_MAP[ct] ?? 'drafts';
}

// ---------------------------------------------------------------------------
// PlaybookLibrary — thin wrapper
// ---------------------------------------------------------------------------

export interface PlaybookLibraryProps {
  briefs: GenerationBrief[];
  briefsLoading: boolean;
  contextClientUid: string;
  contextPersonaId: string;
  onOpenBrief: (id: string) => void;
  onAddBrief: (campaignType: string, clusterId?: string) => void;
  onStartFirstBrief?: () => void;
}

export function PlaybookLibrary({
  briefs: _briefs,
  briefsLoading: _briefsLoading,
  contextClientUid,
  contextPersonaId,
  onOpenBrief,
  onAddBrief,
  onStartFirstBrief,
}: PlaybookLibraryProps) {
  // NOTE: `briefs` and `briefsLoading` come from the generation store which
  // fetches /api/v1/generation/briefs. That endpoint can be slow / time out on
  // large accounts. The SwimLaneBoard has its own independent data source
  // (/api/v1/creative/strategy/swim-lanes) and manages its own loading, error,
  // and empty states — so we must NOT gate its rendering on the brief list
  // result. Showing an "empty" placeholder here when the briefs request merely
  // timed out would hide the entire strategy board.

  if (!contextClientUid) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-white to-teal-50 border border-slate-200 rounded-xl px-6 py-12 text-center">
        <Sparkles size={24} className="mx-auto text-teal-500 mb-3" />
        <h3 className="text-base font-semibold text-navy-900 mb-1">
          Choose a client to view its strategy board.
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          The Strategy board organizes your briefs and creatives into emergent
          strategy clusters, campaigns, personas, or canonical lanes.
        </p>
      </div>
    );
  }

  const handleAddBrief = (lane: SwimLaneData) => {
    // Determine campaign_type to pre-fill: prefer canonical lane id; otherwise
    // use the cluster's canonical_lane_hint via the first matching SWIM_LANE.
    let campaignType = 'drafts';
    if (lane.lane_type === 'canonical') {
      campaignType = SWIM_LANES.find((l) => l.id === lane.lane_id)?.campaignType ?? lane.lane_id;
    } else if (lane.cluster_metadata) {
      campaignType =
        SWIM_LANES.find((l) => l.id === (lane.cluster_metadata as { canonical_lane_hint?: string }).canonical_lane_hint)
          ?.campaignType ?? 'drafts';
    } else if (lane.lane_type === 'campaign') {
      campaignType = 'drafts';
    }
    onAddBrief(campaignType, lane.cluster_metadata?.cluster_id);
  };

  return (
    <SwimLaneBoard
      clientUid={contextClientUid}
      personaId={contextPersonaId || undefined}
      onOpenBrief={onOpenBrief}
      onAddBrief={handleAddBrief}
    />
  );
}
